import type { Step } from '@vrlearning/lesson-schema';
import { GUIDE_PAUSE_SECONDS } from './guide.js';
import type { StepPhase } from './player.js';

/**
 * A demo that plays itself (`pnpm demo:build --autoplay`): built into a demo
 * only (`virtual:demo-play`), never reachable in a lesson a class or a pupil
 * plays -- there is no address or key that turns it on.
 *
 * It does what a pupil would, at the pace the voice guide keeps (ADR-035):
 * an explanation step moves on as the guide's pacer moves it (2 s after the
 * voice and the step's change); a question waits for the voice to finish,
 * then 3 s, then is answered -- a quiz with its right answer, a prediction
 * with the lesson's own answer, so "Jij koos: …" and the reveal that follows
 * agree -- and its outcome is read as the pacer reads it; the last step ends
 * the lesson after its voice, on the demo's end card. Where the voice does
 * not guide (the viewer switched to text, or a voice that never came), it
 * reads the panel's words for as long as they take instead.
 *
 * The rules are pure (`autoplayNext`); `Autoplay` keeps the facts of the
 * step it is on and the one timer, as GuidePacer does.
 */

/** A question is answered this long after the voice has asked it. */
export const AUTOPLAY_ANSWER_SECONDS = 3;
/** The last step ends the lesson this long after its voice. */
export const AUTOPLAY_END_SECONDS = 4;
/** A voice that was due and never ended: acted on this long after the step showed anyway. */
export const AUTOPLAY_FALLBACK_SECONDS = 45;

/** What the demo does next. */
export type AutoplayMove = 'begin' | 'answer' | 'next' | 'finish';

export type AutoplayFacts = {
  phase: StepPhase;
  isLast: boolean;
  /** The step is guided by the voice (render `voice-text` or `voice-subtitles`): the pacer moves it. */
  guided: boolean;
  /** The step has a voice that is going to play: wait for its end. */
  voiceDue: boolean;
  hotspotOpen: boolean;
  /** When this phase began, on the caller's clock. */
  since: number;
  /** When the voice ended (or an answer cut it). */
  voiceEnded: number | undefined;
  /** When the step's change ends; 0 when it has none. */
  changeEnded: number;
  /** How long the panel's words take to read, for a step the voice does not carry. */
  readSeconds: number;
  /** After an answer: when its outcome has been read. */
  readEnded: number | undefined;
};

/** How long `text` takes to read aloud in one's head: 2 s plus 15 characters a second, 4–20 s. */
export function readingSeconds(text: string): number {
  return Math.min(20, Math.max(4, 2 + text.length / 15));
}

/**
 * The option the demo picks: a quiz's right answer; a prediction's own
 * answer when it has one, else its middle option -- the guess most people
 * make between a too-small and a too-big one. Undefined: nothing to answer.
 */
export function autoplayChoice(step: Pick<Step, 'quiz' | 'predict'>): number | undefined {
  if (step.quiz) return step.quiz.answer;
  if (step.predict) {
    const { answer, options } = step.predict;
    return answer ?? Math.floor((options.length - 1) / 2);
  }
  return undefined;
}

/** When the step's words and voice are done: the voice's end, or the reading time without one. */
function settledAt(facts: AutoplayFacts): number {
  const said =
    facts.voiceEnded ??
    (facts.voiceDue ? facts.since + AUTOPLAY_FALLBACK_SECONDS : facts.since + facts.readSeconds);
  return Math.max(said, facts.changeEnded);
}

/**
 * What to do and when (on the caller's clock), or nothing: the pacer has it,
 * a hotspot's card is open, or the step is a task the demo cannot do.
 */
export function autoplayNext(facts: AutoplayFacts): { move: AutoplayMove; at: number } | undefined {
  if (facts.hotspotOpen) return undefined;
  const settled = settledAt(facts);
  // The guide's pacer moves a guided step on; this only stands in when its voice never ended.
  const pacerHasIt = facts.guided && facts.voiceEnded !== undefined && !facts.isLast;
  switch (facts.phase) {
    case 'intro':
      // The guide asks the question when the voice reaches it; this, when it does not.
      return { move: 'begin', at: settled + AUTOPLAY_ANSWER_SECONDS };
    case 'quiz':
    case 'predict':
      return { move: 'answer', at: settled + AUTOPLAY_ANSWER_SECONDS };
    case 'panel':
      if (facts.isLast) return { move: 'finish', at: settled + AUTOPLAY_END_SECONDS };
      return pacerHasIt ? undefined : { move: 'next', at: settled + GUIDE_PAUSE_SECONDS };
    case 'result': {
      const read = facts.readEnded ?? facts.since + facts.readSeconds;
      const at = Math.max(read, settled) + GUIDE_PAUSE_SECONDS;
      if (facts.isLast)
        return { move: 'finish', at: at + AUTOPLAY_END_SECONDS - GUIDE_PAUSE_SECONDS };
      return pacerHasIt && facts.readEnded !== undefined ? undefined : { move: 'next', at };
    }
    case 'task':
      return undefined;
  }
}

export type AutoplayOptions = {
  now: () => number;
  /** Runs `run` after `seconds`; returns a cancel. */
  schedule: (run: () => void, seconds: number) => () => void;
  /** Does it: the caller knows how to answer, begin, move on and finish. */
  act: (move: AutoplayMove) => void;
};

/** The step's facts as they are told, and the one timer that follows from them. */
export class Autoplay {
  readonly #options: AutoplayOptions;
  #facts: AutoplayFacts | undefined;
  #entry = 0;
  #cancel: (() => void) | undefined;
  #planned: { move: AutoplayMove; at: number } | undefined;
  #disposed = false;

  constructor(options: AutoplayOptions) {
    this.#options = options;
  }

  /** What is planned, for the harness and the tests. */
  get planned(): { move: AutoplayMove; at: number } | undefined {
    return this.#planned;
  }

  /** Whether a step has been entered: before that there is nothing to tell it. */
  get active(): boolean {
    return this.#facts !== undefined && !this.#disposed;
  }

  /** A new step entry. */
  enter(
    facts: Omit<
      AutoplayFacts,
      'since' | 'voiceEnded' | 'changeEnded' | 'readEnded' | 'hotspotOpen'
    >,
  ): void {
    this.#entry++;
    this.#facts = {
      ...facts,
      since: this.#options.now(),
      voiceEnded: undefined,
      changeEnded: 0,
      readEnded: undefined,
      hotspotOpen: false,
    };
    this.#plan();
  }

  /** The phase changed within the step (it starts the phase's clock), or what guides it did. */
  update(
    facts: Partial<Pick<AutoplayFacts, 'phase' | 'guided' | 'voiceDue' | 'readSeconds'>>,
  ): void {
    if (!this.#facts) return;
    const phaseChanged = facts.phase !== undefined && facts.phase !== this.#facts.phase;
    this.#facts = {
      ...this.#facts,
      ...facts,
      // A new phase starts its clock; an outcome already being read stays (it may be told first).
      ...(phaseChanged
        ? {
            since: this.#options.now(),
            readEnded: facts.phase === 'result' ? this.#facts.readEnded : undefined,
          }
        : {}),
    };
    this.#plan();
  }

  voiceEnded(): void {
    this.#set({ voiceEnded: this.#options.now() });
  }

  /** The voice starts again ("↻ Nog eens", the guide switched back): wait for it again. */
  restarted(): void {
    this.#set({ voiceEnded: undefined, since: this.#options.now() });
  }

  changeEnds(seconds: number): void {
    this.#set({ changeEnded: this.#options.now() + seconds });
  }

  resultShown(seconds: number): void {
    this.#set({ readEnded: this.#options.now() + seconds });
  }

  hotspot(open: boolean): void {
    this.#set({ hotspotOpen: open, ...(open ? {} : { since: this.#options.now() }) });
  }

  dispose(): void {
    this.#disposed = true;
    this.#clear();
  }

  #set(facts: Partial<AutoplayFacts>): void {
    if (!this.#facts) return;
    this.#facts = { ...this.#facts, ...facts };
    this.#plan();
  }

  #clear(): void {
    this.#cancel?.();
    this.#cancel = undefined;
    this.#planned = undefined;
  }

  #plan(): void {
    this.#clear();
    if (this.#disposed || !this.#facts) return;
    const next = autoplayNext(this.#facts);
    if (!next) return;
    const entry = this.#entry;
    this.#planned = next;
    const cancel = this.#options.schedule(
      () => {
        if (this.#cancel !== cancel || entry !== this.#entry || this.#disposed) return;
        this.#cancel = undefined;
        this.#planned = undefined;
        this.#options.act(next.move);
      },
      Math.max(0, next.at - this.#options.now()),
    );
    this.#cancel = cancel;
  }
}
